/**
 * /frontend/src/features/analytics/pages/StoreAnalytics.js
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import '../../../shared/components/layout/StoreDashboardLayout.css';
import DateRangeFilter from '../components/shared/DateRangeFilter';
import SyncingOverlay from '../components/shared/SyncingOverlay';
import { DATE_PRESETS } from '../constants/datePresets';
import useDateRangeFilter from '../hooks/useDateRangeFilter';
import { clampIsoDate, todayIsoDate, validateIsoDateRange } from '../utils/dateUtils';
import { earliestMerchantPastDateIso } from '../../../shared/utils/merchantHistoryWindow';
import QueueAnalyticsPage from './QueueAnalyticsPage';
import PeakAnalyticsPage from './PeakAnalyticsPage';
import AbandonmentAnalyticsPage from './AbandonmentAnalyticsPage';
import BottleneckAnalyticsPage from './BottleneckAnalyticsPage';
import OfferAnalyticsPage from './OfferAnalyticsPage';
import AppointmentAnalyticsPage from './AppointmentAnalyticsPage';
import CustomerAnalyticsPage from './CustomerAnalyticsPage';
import StaffingAnalyticsPage from './StaffingAnalyticsPage';
import WhatIfAnalyticsPage from './WhatIfAnalyticsPage';
import AnomalyAnalyticsPage from './AnomalyAnalyticsPage';
import { AnalyticsViewContext } from '../components/shared/AnalyticsViewContext';
import './StoreAnalytics.css';

const ANALYTICS_ACTIVE_MODULE_STORAGE_KEY = 'dashboardAnalyticsActiveModule';

const buildModules = (merchantModules) => ([
  { key: 'queue', label: 'Queue', enabled: merchantModules?.queueAnalytics === true },
  { key: 'peak', label: 'Peak', enabled: merchantModules?.peakAnalytics === true },
  { key: 'abandonment', label: 'Abandonment', enabled: merchantModules?.abandonmentAnalytics === true },
  { key: 'bottleneck', label: 'Bottlenecks', enabled: merchantModules?.bottleneckAnalytics === true },
  { key: 'offers', label: 'Offers', enabled: merchantModules?.offersAnalytics === true },
  { key: 'appointments', label: 'Appointments', enabled: merchantModules?.appointmentsAnalytics === true },
  { key: 'customers', label: 'Customers', enabled: merchantModules?.customerBehaviorAnalytics === true },
  { key: 'staffing', label: 'Staffing', enabled: merchantModules?.staffingAnalytics === true },
  { key: 'whatif', label: 'What-if', enabled: merchantModules?.whatIfAnalytics === true },
  { key: 'anomalies', label: 'Alerts', enabled: merchantModules?.anomalyAnalytics === true }
]);

const visibleModules = (modules) => modules.filter((module) => module.enabled);

const firstEnabledKey = (modules) => (visibleModules(modules)[0]?.key || 'queue');

/**
 * Analytics tab container for store dashboard.
 * Driven by `merchantModules` (no token flags inside this module).
 * @param {{request:Function,storeToken:string,storeUuid:string,merchantModules:Object,merchantInfo?:Object}} props
 * @returns {JSX.Element}
 */
const StoreAnalytics = ({ request, storeToken, storeUuid, merchantModules }) => {
  const dateRange = useDateRangeFilter('7d');
  const maxSelectableDate = useMemo(() => todayIsoDate(), []);
  // Merchants may look back at most 6 months anywhere the past is selectable.
  const minSelectableDate = useMemo(() => earliestMerchantPastDateIso(), []);
  const modules = useMemo(() => buildModules(merchantModules || {}), [merchantModules]);
  const visibleAnalyticsModules = useMemo(() => visibleModules(modules), [modules]);
  const [activeKey, setActiveKey] = useState(() => {
    const stored = typeof window !== 'undefined'
      ? String(window.localStorage.getItem(ANALYTICS_ACTIVE_MODULE_STORAGE_KEY) || '').trim()
      : '';
    const storedEnabled = modules.some((module) => module.key === stored && module.enabled);
    return storedEnabled ? stored : firstEnabledKey(modules);
  });
  const [refreshSignal, setRefreshSignal] = useState(1);
  const [appliedStartDate, setAppliedStartDate] = useState(dateRange.startDate);
  const [appliedEndDate, setAppliedEndDate] = useState(dateRange.endDate);
  const [analyticsInFlight, setAnalyticsInFlight] = useState(0);
  const [isFetchModalOpen, setIsFetchModalOpen] = useState(false);
  const [modalStartDate, setModalStartDate] = useState(dateRange.startDate);
  const [modalEndDate, setModalEndDate] = useState(dateRange.endDate);
  const inFlightRef = useRef(new Set());
  const isSyncing = analyticsInFlight > 0;
  const modalDateValidation = useMemo(
    () => validateIsoDateRange(modalStartDate, modalEndDate),
    [modalEndDate, modalStartDate]
  );

  useEffect(() => {
    const shouldTrack = (requestName) => typeof requestName === 'string' && requestName.startsWith('analytics-');
    const inFlightSet = inFlightRef.current;

    const handleStart = (event) => {
      const requestId = event?.detail?.requestId;
      const requestName = event?.detail?.requestName;
      if (!requestId || !shouldTrack(requestName)) {
        return;
      }
      if (inFlightSet.has(requestId)) {
        return;
      }
      inFlightSet.add(requestId);
      setAnalyticsInFlight((prev) => prev + 1);
    };

    const handleEnd = (event) => {
      const requestId = event?.detail?.requestId;
      const requestName = event?.detail?.requestName;
      if (!requestId || !shouldTrack(requestName)) {
        return;
      }
      if (!inFlightSet.has(requestId)) {
        return;
      }
      inFlightSet.delete(requestId);
      setAnalyticsInFlight((prev) => Math.max(0, prev - 1));
    };

    window.addEventListener('api-request-start', handleStart);
    window.addEventListener('api-request-end', handleEnd);
    return () => {
      window.removeEventListener('api-request-start', handleStart);
      window.removeEventListener('api-request-end', handleEnd);
      inFlightSet.clear();
      setAnalyticsInFlight(0);
    };
  }, []);

  useEffect(() => {
    setActiveKey((prev) => {
      const prevEnabled = modules.some((m) => m.key === prev && m.enabled);
      return prevEnabled ? prev : firstEnabledKey(modules);
    });
  }, [modules]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(ANALYTICS_ACTIVE_MODULE_STORAGE_KEY, activeKey);
  }, [activeKey]);

  useEffect(() => {
    if (!isFetchModalOpen) {
      return undefined;
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsFetchModalOpen(false);
      }
    };
    document.body.classList.add('modal-open');
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isFetchModalOpen]);

  const handlePresetChange = (presetKey) => {
    const preset = DATE_PRESETS.find((item) => item.key === presetKey);
    if (!preset) {
      return;
    }
    const nextRange = preset.resolve();
    dateRange.setPresetKey(presetKey);
    dateRange.setStartDate(nextRange.startDate);
    dateRange.setEndDate(nextRange.endDate);
    setAppliedStartDate(nextRange.startDate);
    setAppliedEndDate(nextRange.endDate);
    setRefreshSignal((value) => value + 1);
  };

  const openCustomDateModal = () => {
    setModalStartDate(appliedStartDate || dateRange.startDate);
    setModalEndDate(appliedEndDate || dateRange.endDate);
    setIsFetchModalOpen(true);
  };

  const handleModalStartDateChange = (value) => {
    const nextStart = clampIsoDate(value, { min: minSelectableDate, max: maxSelectableDate });
    setModalStartDate(nextStart);
  };

  const handleModalEndDateChange = (value) => {
    const nextEnd = clampIsoDate(value, { min: minSelectableDate, max: maxSelectableDate });
    setModalEndDate(nextEnd);
  };

  const handleFetchConfirm = () => {
    if (!modalDateValidation.valid) {
      return;
    }
    dateRange.setPresetKey('custom');
    dateRange.setStartDate(modalStartDate);
    dateRange.setEndDate(modalEndDate);
    setAppliedStartDate(modalStartDate);
    setAppliedEndDate(modalEndDate);
    setRefreshSignal((value) => value + 1);
    setIsFetchModalOpen(false);
  };

  const analyticsView = useMemo(() => ({
    appliedStartDate,
    appliedEndDate,
    isSampleData: true
  }), [appliedEndDate, appliedStartDate]);

  return (
    <AnalyticsViewContext.Provider value={analyticsView}>
      <div className="analytics-shell">
        <section className="dashboard-tab-hero">
          <div className="dashboard-tab-hero-copy">
            <p className="dashboard-tab-hero-eyebrow">Store Insights</p>
            <h3 className="dashboard-tab-hero-title">Analytics Console</h3>
            <p className="dashboard-tab-hero-subtitle">
              Operational insights for performance, demand, and customer flow.
            </p>
            <div className="dashboard-tab-hero-tags">
              <span className="dashboard-tab-hero-tag">Decision intelligence</span>
              <span className="dashboard-tab-hero-tag">Operational trends</span>
            </div>
          </div>
          <div className="dashboard-tab-hero-actions">
            <div className="analytics-hero-actions">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                presetKey={dateRange.presetKey}
                onPresetChange={handlePresetChange}
                onCustomClick={openCustomDateModal}
                minDate={minSelectableDate}
                maxDate={maxSelectableDate}
                showCustomFields={false}
                disabled={isSyncing}
              />
            </div>
          </div>
        </section>

        <div className="analytics-shell-body">
          <div className="analytics-shell-tabs" role="tablist" aria-label="Analytics modules">
            {visibleAnalyticsModules.map((m) => (
              <button
                key={m.key}
                type="button"
                role="tab"
                className={`analytics-nav-item ${activeKey === m.key ? 'active' : ''}`}
                onClick={() => {
                  setActiveKey(m.key);
                }}
                aria-selected={activeKey === m.key}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className="analytics-shell-content" role="tabpanel">
            <div className="analytics-shell-panel">
              <div className={`analytics-shell-panel-content ${isSyncing ? 'is-syncing' : ''}`}>
                {activeKey === 'queue' ? (
                  <QueueAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'peak' ? (
                  <PeakAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'abandonment' ? (
                  <AbandonmentAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'bottleneck' ? (
                  <BottleneckAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'offers' ? (
                  <OfferAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'appointments' ? (
                  <AppointmentAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'customers' ? (
                  <CustomerAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'staffing' ? (
                  <StaffingAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'whatif' ? (
                  <WhatIfAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
                {activeKey === 'anomalies' ? (
                  <AnomalyAnalyticsPage
                    request={request}
                    storeToken={storeToken}
                    storeUuid={storeUuid}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    refreshSignal={refreshSignal}
                  />
                ) : null}
              </div>
              {isSyncing ? <SyncingOverlay /> : null}
            </div>
          </div>
        </div>
      </div>
      {isFetchModalOpen ? (
        <div
          className="zone-modal-overlay analytics-fetch-modal-overlay"
          role="presentation"
          onMouseDown={() => setIsFetchModalOpen(false)}
        >
          <div
            className="zone-modal analytics-fetch-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="analytics-fetch-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="zone-modal-header">
              <div>
                <h3 id="analytics-fetch-modal-title">Fetch analytics data</h3>
                <p>Select the date range to load analytics.</p>
              </div>
              <button
                type="button"
                className="analytics-fetch-modal-close"
                aria-label="Close fetch modal"
                onClick={() => setIsFetchModalOpen(false)}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
            <div className="zone-modal-body analytics-fetch-modal-body">
              <div className="analytics-date-field">
                <label htmlFor="analytics-fetch-start-date">Start date</label>
                <input
                  id="analytics-fetch-start-date"
                  type="date"
                  value={modalStartDate}
                  min={minSelectableDate}
                  max={modalEndDate || maxSelectableDate}
                  onChange={(event) => handleModalStartDateChange(event.target.value)}
                />
              </div>
              <div className="analytics-date-field">
                <label htmlFor="analytics-fetch-end-date">End date</label>
                <input
                  id="analytics-fetch-end-date"
                  type="date"
                  value={modalEndDate}
                  min={modalStartDate || minSelectableDate}
                  max={maxSelectableDate}
                  onChange={(event) => handleModalEndDateChange(event.target.value)}
                />
              </div>
              {!modalDateValidation.valid ? (
                <p className="analytics-date-error">{modalDateValidation.message}</p>
              ) : null}
            </div>
            <div className="zone-modal-actions">
              <button
                type="button"
                className="btn ghost small"
                onClick={() => setIsFetchModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn primary small"
                onClick={handleFetchConfirm}
                disabled={!modalDateValidation.valid || isSyncing}
              >
                Fetch
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AnalyticsViewContext.Provider>
  );
};

export default StoreAnalytics;
